import { useMemo, useState } from 'react';

import { useFormik } from 'formik';

import { formatDate } from 'lib/helpers/dateUtils';

import {
  usePaymentContext,
  usePaymentContextApi
} from 'components/Payments/NewPayment/PaymentContent/context/PaymentContext';

import { PreAuthModalValidationSchema } from './validation';

const isFormValid = (values) => {
  try {
    PreAuthModalValidationSchema.validateSync(values);
    return true;
  } catch {
    return false;
  }
};

export const usePreAuthForm = ({
  appointment,
  patientName,
  practiceId,
  patientId,
  timezone,
  preAuthAmount = null,
  defaultScope = null
}) => {
  const { handlePayment } = usePaymentContextApi();
  const { processor, values } = usePaymentContext();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isPayroc = processor === 'payroc';

  const stampDate = () => formatDate(new Date(), timezone);

  const initialValues = useMemo(
    () => ({
      cardholderName: !isPayroc
        ? patientName ||
          [appointment?.patient?.f_name, appointment?.patient?.l_name].filter(Boolean).join(' ')
        : '',
      signature: '',
      date: formatDate(new Date()),
      type: appointment?.type || 'annual',
      practiceId: practiceId || appointment?.practice?.id || '',
      patientId: patientId || appointment?.patient?.id || '',
      appointmentId: appointment?.id || null,
      cardId: null,
      address_ln_1: appointment?.patient?.userAddress?.address_ln_1 || '',
      address_ln_2: appointment?.patient?.userAddress?.address_ln_2 || '',
      city: appointment?.patient?.userAddress?.city || '',
      state: appointment?.patient?.userAddress?.state || '',
      zip: appointment?.patient?.userAddress?.zip || '',
      authorized: false,
      understood: false,
      scope: defaultScope,
      processor
    }),
    [appointment, processor, patientName, practiceId, patientId, isPayroc, defaultScope]
  );

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: PreAuthModalValidationSchema,
    validateOnMount: true,
    revalidateOnChange: true,
    validateOnBlur: false
  });

  const submit = async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length) return;

    setIsSubmitting(true);
    try {
      const signatureDate = stampDate();
      await formik.setFieldValue('date', signatureDate);

      const isRecurring = values?.method === 'recurring-charge';
      const preAuthSaveAmount =
        !isRecurring && Number.isFinite(Number(preAuthAmount)) ? Number(preAuthAmount) : null;

      await handlePayment({
        preAuthForm: {
          ...formik.values,
          date: signatureDate,
          amount: preAuthSaveAmount,
          billingAddress: {
            address_ln_1: formik?.values?.address_ln_1 || '',
            address_ln_2: formik?.values?.address_ln_2 || '',
            city: formik?.values?.city || '',
            state: formik?.values?.state || '',
            zip: formik?.values?.zip || ''
          }
        }
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDisabled = !isFormValid(formik.values) || isSubmitting;

  return { formik, isPayroc, submit, isDisabled, stampDate };
};
